import {Injectable} from '@angular/core';
import {Observable, of, throwError, zip} from 'rxjs';
import {
  BehaviorSubjectService,
  LicenseType,
  MediaType,
  Product,
  ProductOffering,
  ProductService,
  User
} from '@uofu-uss-aux/software-catalog-ng-core';
import {first, map, tap} from 'rxjs/operators';
import {CartDataService} from './cart-data.service';
import {Cart, CartItem, CartItemStatus, CartStatus, CartTotals, PaymentMethod} from './Cart';
import {clone, cloneDeep, keyBy} from 'lodash-es';

export interface LineItem {
  cart: Cart;
  cartItem: CartItem;
  product: Product;
  offering: ProductOffering;
}

export interface CartWithLineItems {
  cart: Cart;
  lineItems: ReadonlyArray<LineItem>;
}

export enum InvalidItem {
  Student,
  License
}

@Injectable({
  providedIn: 'root'
})
export class CartService extends BehaviorSubjectService<Cart> {

  constructor(private readonly cartDataService: CartDataService,
              private readonly productService: ProductService) {
    super(null);
  }

  fetchCurrentShoppingCart(unid: string): Observable<Cart> {
    return this.cartDataService.fetchExistingCarts([CartStatus.SessionCart], unid)
      .pipe(first(),
        map(carts => (carts && carts.length) ? carts[0] : null));
  }

  fetchCarts(statuses: ReadonlyArray<CartStatus>, unid: string): Observable<ReadonlyArray<Cart>> {
    if (!unid) {
      return throwError(() => 'Unable to fetch a user without a unid');
    }

    return this.cartDataService.fetchExistingCarts(statuses, unid);
  }

  checkout(cart: Cart, paymentMethod = PaymentMethod.CreditCard): Observable<Cart> {
    return this.cartDataService.checkout(cart, paymentMethod);
  }

  updateCart(cart: Cart): Observable<Cart> {
    return this.cartDataService.updateCart(cart);
  }

  fetchCartWithId(cartId: number): Observable<Cart> {
    return this.cartDataService.fetchCart(cartId);
  }

  fetchLineItems(cart: Cart): Observable<ReadonlyArray<LineItem>> {
    if (!cart.items || !cart.items.length) {
      return of([]);
    }

    const productObs = cart.items.map(item => this.productService.fetchProduct(item.productId));
    return zip(...productObs).pipe(map(products => this.mapToLineItem(cart, products)));
  }

  private mapToLineItem(cart: Cart, products: ReadonlyArray<Product>): ReadonlyArray<LineItem> {
    const productMap = keyBy(products, 'id');
    return cart.items.map(item => this.generateLineItem(cart, item, productMap));
  }

  private generateLineItem(cart: Cart, cartItem: CartItem, productMap: { [key: number]: Product }): LineItem {
    const product = productMap[cartItem.productId];
    return {
      cart,
      cartItem,
      product,
      offering: this.productService.findOfferingFromProduct(product, cartItem.productOfferingId),
    };
  }

  offerOfType(offering: ProductOffering, product: Product, licenseType: LicenseType): boolean {
    return !!this.productService.findLicense(product, licenseType)?.offerings.find((po) => offering.id === po.id);
  }

  canBeAddedToCart(cart: Cart, offering: ProductOffering, product: Product, user: User):
    Observable<{ canBeAdded: boolean, type: InvalidItem }> {
    if (!user?.student && this.offerOfType(offering, product, LicenseType.Student)) {
      return of({canBeAdded: false, type: InvalidItem.Student});
    }
    const isOfferUniv = this.offerOfType(offering, product, LicenseType.University);

    return this.fetchLineItems(cart).pipe(
      map((lineItems) => {
        const hasAnyUnivLicenses = !!lineItems.find(lineItem =>
          this.offerOfType(lineItem.offering, lineItem.product, LicenseType.University));
        const canBeAdded = !lineItems.length || hasAnyUnivLicenses === isOfferUniv;
        return {canBeAdded, type: canBeAdded ? undefined : InvalidItem.License};
      })
    );
  }

  addToCart(offering: ProductOffering, cart: Cart): Observable<Cart> {
    if (!offering || !cart) {
      return throwError(() => `Unable to add offering ${offering?.id} to cart with ${cart?.id}`);
    }

    const found = this.offeringFromCart(offering, cart);
    if (found) {
      return this.updateCartItemQty(found, found.quantity + 1, cart);
    }

    cart = clone(cart);
    const item = {
      cartId: cart.id,
      productOfferingId: offering.id,
      quantity: 1,
      status: CartItemStatus.Open,
      downloadable: offering.media === MediaType.Download,
    } as CartItem;

    return this.cartDataService.addItem(item)
      .pipe(
        tap(added => {
          cart.items = cart.items.concat(added);
          console.debug(`Added item: ${added.id}`);
        }),
        map(() => cart)
      );
  }

  offeringFromCart(offering: ProductOffering, cart: Cart): CartItem {
    return cart.items.find(item => item.productOfferingId === offering.id);
  }

  updateCartItemQty(item: CartItem, qty: number, cart: Cart): Observable<Cart> {
    if (!item || qty < 0 || !cart) {
      return throwError(() => `Unable to update item ${item} in cart ${cart} with quantity ${qty}`);
    }

    cart = cloneDeep(cart);
    if (qty === 0) {
      return this.removeFromCart(item.id, cart);
    }

    const updatedValue: CartItem = clone(item);
    updatedValue.quantity = qty;

    return this.cartDataService.updateItem(updatedValue)
      .pipe(
        tap(updated => cart.items = this.replaceItem(updated, cart.items)),
        map(() => cart)
      );
  }

  replaceItem(item: CartItem, items: ReadonlyArray<CartItem>): ReadonlyArray<CartItem> {
    return items.map(current => current.id === item.id ? item : current);
  }

  removeFromCart(cartItemId: number, cart: Cart): Observable<Cart> {
    if (!cartItemId || !cart) {
      return throwError(() => `Unable to remove item ${cartItemId} from cart ${cart}`);
    }

    cart = cloneDeep(cart);
    const index = cart.items ? cart.items.findIndex(item => item.id === cartItemId) : -1;
    if (index > -1) {
      return this.cartDataService.removeItem(cartItemId)
        .pipe(
          tap(() => {
            cart.items = cart.items.filter(current => current.id !== cartItemId);
            cart.items.forEach(item => {
              console.debug(`After remove, cart contains item: ${item.id}`);
            });
          }),
          map(() => cart));
    }
    return throwError(() => `unable to remove cart item ${cartItemId} as it is not in the cart.`);
  }

  calcTotals(cart: Cart): CartTotals {
    let subtotal = 0;
    let tax = 0;
    cart?.items?.forEach(item => {
      subtotal += item.price;
      tax += item.tax;
    });
    const total = subtotal + tax;
    return {subtotal, tax, total};
  }
}
